import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';

/**
 * App updates — upload, publish and track the Android APK we host ourselves.
 * Backend: backend/routes/appReleaseRoutes.js (/api/app/releases).
 */

const CHUNK_TRIES = 3;
const STATUS_COLOURS = {
  uploading: '#d97706',
  ready: '#2563eb',
  published: '#16a34a',
  superseded: '#6b7280',
  withdrawn: '#dc2626',
};

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 };
const greyBtn = { background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' };
const smallBtn = { ...greyBtn, padding: '6px 10px', fontSize: 12 };
const primaryBtn = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontWeight: 700, cursor: 'pointer' };
const dangerBtn = { ...smallBtn, color: '#b91c1c', borderColor: '#fecaca', background: '#fef2f2' };
const textInput = { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13 };
const fieldLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };

const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');
const pct = (n, total) => (total ? `${Math.round((n / total) * 100)}%` : '—');
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function sha256Hex(file) {
  if (!window.crypto?.subtle) throw new Error('This browser cannot compute SHA-256 here (needs HTTPS).');
  const buf = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function Badge({ children, colour = '#6b7280' }) {
  return (
    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: colour, border: `1px solid ${colour}55`, background: `${colour}12`, borderRadius: 999, padding: '1px 7px', marginRight: 4 }}>
      {children}
    </span>
  );
}

function Toggle({ on, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ width: 44, height: 24, borderRadius: 999, border: 'none', position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', padding: 2, opacity: disabled ? 0.55 : 1, background: on ? '#22c55e' : '#cbd5e1', flexShrink: 0 }}>
      <span style={{ display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'transform 0.15s', transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
    </button>
  );
}

function ToggleRow({ label, hint, on, onClick, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid #eef0f3', borderRadius: 10, padding: 10, background: '#fafafa' }}>
      <div>
        <strong style={{ fontSize: 13, display: 'block' }}>{label}</strong>
        {hint ? <span style={{ color: '#6b7280', fontSize: 12 }}>{hint}</span> : null}
      </div>
      <Toggle on={on} onClick={onClick} disabled={disabled} />
    </div>
  );
}

function StatCard({ label, value, colour = '#111827', hint }) {
  return (
    <div style={{ border: '1px solid #eef0f3', borderRadius: 10, padding: 12, background: '#fafafa' }}>
      <span style={{ fontSize: 12, color: '#6b7280', display: 'block' }}>{label}</span>
      <strong style={{ fontSize: 22, color: colour }}>{value}</strong>
      {hint ? <span style={{ fontSize: 11, color: '#9ca3af', display: 'block' }}>{hint}</span> : null}
    </div>
  );
}

function KeyCountTable({ title, rows }) {
  return (
    <div style={{ border: '1px solid #eef0f3', borderRadius: 10, padding: 10 }}>
      <strong style={{ fontSize: 13 }}>{title}</strong>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
        <tbody>
          {(rows || []).length ? rows.map((r) => (
            <tr key={r.key}>
              <td style={{ ...td, padding: '4px 6px' }}>{r.key}</td>
              <td style={{ ...td, padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{r.count}</td>
            </tr>
          )) : (
            <tr><td style={{ ...td, color: '#9ca3af' }}>No data yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function AppReleasesScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(() => ({ Authorization: `Bearer ${userInfo?.token}` }), [userInfo]);
  const base = `${api}/api/app/releases`;

  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState({ latestBuild: 0, minSupportedBuild: 0, forceUpdate: false, blockOldBuildLogin: false });
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  // upload
  const [file, setFile] = useState(null);
  const [versionName, setVersionName] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState(null); // { phase, pct, text }

  // publish dialog
  const [publishing, setPublishing] = useState(null); // release row
  const [pubOpts, setPubOpts] = useState({ force: false, blockOld: false, notify: true, message: '' });

  // policy
  const [minBuildInput, setMinBuildInput] = useState('');

  // devices
  const [filters, setFilters] = useState({ build: '', platform: '', model: '', network: '', outdated: '', q: '' });
  const [devices, setDevices] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [devicePage, setDevicePage] = useState(1);

  const load = useCallback(async () => {
    try {
      const [rel, st] = await Promise.all([
        axios.get(base, { headers }),
        axios.get(`${base}/stats`, { headers }),
      ]);
      setRows(rel.data?.rows || []);
      setCurrent(rel.data?.current || {});
      setMinBuildInput(String(rel.data?.current?.minSupportedBuild ?? 0));
      setStats(st.data || null);
      const maxBuild = Math.max(Number(rel.data?.current?.latestBuild) || 0, ...(rel.data?.rows || []).map((r) => Number(r.buildNumber) || 0), 0);
      setBuildNumber((b) => (b === '' ? String(maxBuild + 1) : b));
    } catch (err) {
      toast.error(getError(err));
    }
  }, [base, headers]);

  const loadDevices = useCallback(async (page) => {
    try {
      const params = { page, pageSize: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (String(v).trim() !== '') params[k] = String(v).trim(); });
      const { data } = await axios.get(`${base}/devices`, { headers, params });
      setDevices(data || { rows: [], page: 1, pages: 1, count: 0 });
    } catch (err) {
      toast.error(getError(err));
    }
  }, [base, headers, filters]);

  useEffect(() => { void load(); }, [load]);
  // Debounced, so typing in the search box is one request, not one per key.
  useEffect(() => {
    const t = setTimeout(() => { void loadDevices(devicePage); }, 300);
    return () => clearTimeout(t);
  }, [loadDevices, devicePage]);

  /* ------------------------------------------------------------ upload */
  const putChunk = async (id, index, blob) => {
    let lastErr;
    for (let attempt = 1; attempt <= CHUNK_TRIES; attempt += 1) {
      try {
        const { data } = await axios.put(`${base}/${id}/chunk`, blob, {
          headers: { ...headers, 'Content-Type': 'application/octet-stream' },
          params: { index },
          timeout: 120000,
        });
        return data;
      } catch (err) {
        lastErr = err;
        // A 4xx other than a timeout will not get better by retrying.
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) break;
        await sleep(800 * attempt);
      }
    }
    throw lastErr;
  };

  const upload = async () => {
    if (!file) return toast.error('Choose the .apk file first.');
    if (!versionName.trim()) return toast.error('Enter the version name (e.g. 1.4.0).');
    const build = Number(buildNumber);
    if (!Number.isInteger(build) || build <= 0) return toast.error('Build number must be a whole number above 0.');
    if (!/\.apk$/i.test(file.name) && !window.confirm('This file does not end in .apk. Upload it anyway?')) return;

    setBusy(true);
    try {
      setProgress({ pct: 0, text: 'Checking the file (SHA-256)…' });
      const sha256 = await sha256Hex(file);

      setProgress({ pct: 0, text: 'Starting upload…' });
      const { data: started } = await axios.post(`${base}/upload/start`, {
        platform: 'android',
        versionName: versionName.trim(),
        buildNumber: build,
        notes,
        fileName: file.name,
        size: file.size,
        sha256,
      }, { headers });
      const id = started.release._id;
      const chunkSize = Number(started.chunkSize) || 524288;
      const total = Math.ceil(file.size / chunkSize);

      for (let i = 0; i < total; i += 1) {
        const blob = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
        const r = await putChunk(id, i, blob);
        const done = Number(r?.receivedBytes) || (i + 1) * chunkSize;
        setProgress({ pct: Math.min(100, Math.round((done / file.size) * 100)), text: `Uploading ${fmtSize(done)} of ${fmtSize(file.size)} (part ${i + 1}/${total})` });
      }

      setProgress({ pct: 100, text: 'Verifying on the server…' });
      await axios.post(`${base}/${id}/complete`, {}, { headers });
      toast.success(`Version ${versionName} (build ${build}) uploaded and verified. Publish it when you are ready.`);
      setFile(null);
      setVersionName('');
      setNotes('');
      setBuildNumber('');
      setProgress(null);
      const input = document.getElementById('apk-file-input');
      if (input) input.value = '';
      await load();
    } catch (err) {
      setProgress((p) => (p ? { ...p, text: `Failed: ${getError(err) || err.message}` } : null));
      toast.error(getError(err) || err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ----------------------------------------------------------- actions */
  const openPublish = (r) => {
    setPublishing(r);
    setPubOpts({ force: false, blockOld: false, notify: true, message: '' });
  };

  const publish = async () => {
    if (!publishing) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${base}/${publishing._id}/publish`, pubOpts, { headers });
      const pushed = data?.pushed ? ` Notified ${data.pushed.delivered} of ${data.pushed.targeted} device(s).` : '';
      toast.success(`${data?.message || 'Published.'}${pushed}`);
      setPublishing(null);
      await load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (r) => {
    if (!window.confirm(`Withdraw version ${r.versionName} (build ${r.buildNumber})? Phones will stop being offered it.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${base}/${r._id}/withdraw`, {}, { headers });
      toast.info(data?.message || 'Withdrawn.', { autoClose: 10000 });
      await load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete version ${r.versionName} (build ${r.buildNumber}) and its file? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${base}/${r._id}`, { headers });
      toast.success(data?.message || 'Deleted.');
      await load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadLink = (r) => {
    const origin = api || window.location.origin;
    return `${origin}/api/app/releases/${r._id}/download`;
  };

  const copyLink = async (r) => {
    const link = downloadLink(r);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Download link copied.');
    } catch {
      window.prompt('Copy this link:', link);
    }
  };

  const savePolicy = async (patch) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${base}/policy`, patch, { headers });
      setCurrent({
        latestBuild: data.latestBuild,
        minSupportedBuild: data.minSupportedBuild,
        forceUpdate: data.forceUpdate,
        blockOldBuildLogin: data.blockOldBuildLogin,
      });
      setMinBuildInput(String(data.minSupportedBuild ?? 0));
      toast.success(data?.message || 'Saved.');
      const st = await axios.get(`${base}/stats`, { headers });
      setStats(st.data || null);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const pushReminder = async () => {
    const outdated = stats?.totals?.outdated || 0;
    if (!window.confirm(`Send an "update available" notification to the ${outdated} outdated device(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${base}/push`, {}, { headers });
      toast.success(data?.message || `Sent to ${data?.delivered || 0} device(s).`);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const totals = stats?.totals || {};
  const latest = Number(current.latestBuild) || 0;
  const minBuild = Number(current.minSupportedBuild) || 0;
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setDevicePage(1); };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 80px' }}>
      <Helmet><title>App updates</title></Helmet>
      <div style={card}>
        <h3 style={{ margin: 0 }}>App updates</h3>
        <p style={{ margin: '4px 0 0', color: '#6b7280' }}>
          Upload a new Android build, publish it, and phones are offered the update on their next check. Members can also download it from <a href="/download-app" target="_blank" rel="noreferrer">/download-app</a>.
        </p>
      </div>

      {/* 1. Upload */}
      <div style={card}>
        <h5 style={{ margin: '0 0 10px' }}>1. Upload a new version</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <span style={fieldLabel}>APK file</span>
            <input id="apk-file-input" type="file" accept=".apk,application/vnd.android.package-archive" disabled={busy} onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {file ? <span style={{ fontSize: 12, color: '#6b7280', display: 'block', marginTop: 4 }}>{file.name} · {fmtSize(file.size)}</span> : null}
          </div>
          <div>
            <span style={fieldLabel}>Version name</span>
            <input style={textInput} value={versionName} maxLength={40} placeholder="e.g. 1.4.0" disabled={busy} onChange={(e) => setVersionName(e.target.value)} />
          </div>
          <div>
            <span style={fieldLabel}>Build number (must be higher than every earlier build)</span>
            <input style={textInput} type="number" min={1} value={buildNumber} disabled={busy} onChange={(e) => setBuildNumber(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <span style={fieldLabel}>What is new (one line per point — shown in the app)</span>
          <textarea style={{ ...textInput, minHeight: 90 }} value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} placeholder={'Faster wallet screen\nFixed a crash when opening notifications'} />
        </div>
        {progress ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ height: 10, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${progress.pct}%`, height: '100%', background: '#2563eb', transition: 'width 0.2s' }} />
            </div>
            <span style={{ fontSize: 12, color: '#374151', display: 'block', marginTop: 4 }}>{progress.text}</span>
          </div>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <button type="button" style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={upload}>{busy && progress ? 'Uploading…' : 'Upload'}</button>
        </div>
      </div>

      {/* 2. Releases */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0 }}>2. Versions</h5>
          <button type="button" style={smallBtn} onClick={load} disabled={busy}>Refresh</button>
        </div>
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Version</th><th style={th}>Build</th><th style={th}>Size</th><th style={th}>Status</th>
                <th style={th}>Downloads</th><th style={th}>Published</th><th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((r) => (
                <tr key={r._id}>
                  <td style={td}>
                    <strong>{r.versionName}</strong>
                    {r.notes ? <span style={{ display: 'block', fontSize: 11, color: '#6b7280', whiteSpace: 'pre-line', maxWidth: 260 }}>{r.notes}</span> : null}
                  </td>
                  <td style={td}>{r.buildNumber}</td>
                  <td style={td}>{fmtSize(r.size)}</td>
                  <td style={td}>
                    <Badge colour={STATUS_COLOURS[r.status]}>{r.status}</Badge>
                    {r.status === 'published' && r.force ? <Badge colour="#dc2626">forced</Badge> : null}
                    {r.status === 'published' && r.blockOld ? <Badge colour="#7c3aed">old blocked</Badge> : null}
                    {r.status === 'uploading' ? <span style={{ display: 'block', fontSize: 11, color: '#9ca3af' }}>{pct(r.receivedBytes, r.size)} received</span> : null}
                  </td>
                  <td style={td}>{r.downloads || 0}</td>
                  <td style={td}>{fmtDate(r.publishedAt)}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {['ready', 'superseded', 'withdrawn'].includes(r.status) ? (
                        <button type="button" style={{ ...smallBtn, background: '#dcfce7', borderColor: '#86efac' }} disabled={busy} onClick={() => openPublish(r)}>Publish</button>
                      ) : null}
                      {['published', 'superseded', 'ready'].includes(r.status) ? (
                        <button type="button" style={smallBtn} disabled={busy} onClick={() => withdraw(r)}>Withdraw</button>
                      ) : null}
                      {r.status !== 'published' ? (
                        <button type="button" style={dangerBtn} disabled={busy} onClick={() => remove(r)}>Delete</button>
                      ) : null}
                      {['published', 'superseded'].includes(r.status) ? (
                        <button type="button" style={smallBtn} onClick={() => copyLink(r)}>Copy link</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td style={{ ...td, color: '#9ca3af' }} colSpan={7}>No versions uploaded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {publishing ? (
          <div style={{ marginTop: 14, border: '1px solid #93c5fd', background: '#eff6ff', borderRadius: 12, padding: 14 }}>
            <strong>Publish version {publishing.versionName} (build {publishing.buildNumber})</strong>
            <p style={{ margin: '4px 0 10px', color: '#4b5563', fontSize: 13 }}>Phones below this build will be offered the update on their next check.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
              <ToggleRow label="Force the update" hint="Older builds get a screen they cannot dismiss (sets the minimum build to this one)." on={pubOpts.force} onClick={() => setPubOpts((o) => ({ ...o, force: !o.force }))} />
              <ToggleRow label="Block sign-in on older versions" hint="Older builds cannot log in at all until they update." on={pubOpts.blockOld} onClick={() => setPubOpts((o) => ({ ...o, blockOld: !o.blockOld }))} />
              <ToggleRow label="Notify outdated devices now" hint="Push notification to every phone on an older build." on={pubOpts.notify} onClick={() => setPubOpts((o) => ({ ...o, notify: !o.notify }))} />
            </div>
            <div style={{ marginTop: 10 }}>
              <span style={fieldLabel}>Message shown on the update screen (blank = default)</span>
              <input style={textInput} maxLength={300} value={pubOpts.message} onChange={(e) => setPubOpts((o) => ({ ...o, message: e.target.value }))} placeholder="A new version of Icon Marketing is ready. Please update to continue." />
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button type="button" style={{ ...primaryBtn, background: '#16a34a' }} disabled={busy} onClick={publish}>{busy ? 'Publishing…' : 'Publish now'}</button>
              <button type="button" style={greyBtn} disabled={busy} onClick={() => setPublishing(null)}>Cancel</button>
            </div>
          </div>
        ) : null}
      </div>

      {/* 3. Policy */}
      <div style={card}>
        <h5 style={{ margin: '0 0 10px' }}>3. Update policy</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
          <StatCard label="Latest build offered" value={latest || '—'} />
          <StatCard label="Minimum supported build" value={minBuild || 'none'} colour={minBuild ? '#b45309' : '#111827'} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
          <ToggleRow label="Force update now" hint="Every build below the latest sees a screen it cannot dismiss." on={Boolean(current.forceUpdate)} disabled={busy} onClick={() => savePolicy({ forceUpdate: !current.forceUpdate })} />
          <ToggleRow label="Block login on builds below the minimum" hint={minBuild ? `Builds below ${minBuild} cannot sign in.` : 'Has no effect until a minimum build is set.'} on={Boolean(current.blockOldBuildLogin)} disabled={busy} onClick={() => savePolicy({ blockOldBuildLogin: !current.blockOldBuildLogin })} />
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: 200 }}>
            <span style={fieldLabel}>Minimum supported build (0 = none)</span>
            <input style={textInput} type="number" min={0} value={minBuildInput} onChange={(e) => setMinBuildInput(e.target.value)} />
          </div>
          <button type="button" style={greyBtn} disabled={busy} onClick={() => savePolicy({ minSupportedBuild: Number(minBuildInput) || 0 })}>Save minimum</button>
        </div>
      </div>

      {/* 4. Stats */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0 }}>4. Who is on which version</h5>
          <button type="button" style={{ ...primaryBtn, padding: '8px 14px' }} disabled={busy || !totals.outdated} onClick={pushReminder}>Push update reminder to outdated devices</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 12 }}>
          <StatCard label="Devices" value={totals.devices || 0} />
          <StatCard label="Active 7 days" value={totals.active7 || 0} />
          <StatCard label="Active 30 days" value={totals.active30 || 0} />
          <StatCard label="On latest" value={totals.onLatest || 0} colour="#16a34a" hint={pct(totals.onLatest || 0, totals.devices)} />
          <StatCard label="Outdated" value={totals.outdated || 0} colour="#d97706" hint={pct(totals.outdated || 0, totals.devices)} />
          <StatCard label="Below minimum" value={totals.belowMinimum || 0} colour="#dc2626" />
        </div>
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Build</th><th style={th}>Version</th><th style={th}>Devices</th><th style={th}>% of devices</th>
                <th style={th}>Users</th><th style={th}>Active 7d</th><th style={th}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.byVersion || []).length ? stats.byVersion.map((v) => {
                const old = latest && v.buildNumber < latest;
                const below = minBuild && v.buildNumber < minBuild;
                return (
                  <tr key={v.buildNumber} style={{ background: below ? '#fef2f2' : old ? '#fffbeb' : undefined }}>
                    <td style={td}>
                      <strong>{v.buildNumber || 'unknown'}</strong>{' '}
                      {v.buildNumber === latest && latest ? <Badge colour="#16a34a">latest</Badge> : null}
                      {below ? <Badge colour="#dc2626">below minimum</Badge> : old ? <Badge colour="#d97706">outdated</Badge> : null}
                    </td>
                    <td style={td}>{v.appVersion || '—'}</td>
                    <td style={td}>{v.devices}</td>
                    <td style={td}>{pct(v.devices, totals.devices)}</td>
                    <td style={td}>{v.users}</td>
                    <td style={td}>{v.active7}</td>
                    <td style={td}>{fmtDate(v.lastSeenAt)}</td>
                  </tr>
                );
              }) : (
                <tr><td style={{ ...td, color: '#9ca3af' }} colSpan={7}>No devices have checked in yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 14 }}>
          <KeyCountTable title="Platform" rows={stats?.byPlatform} />
          <KeyCountTable title="Phone model (top 20)" rows={stats?.byModel} />
          <KeyCountTable title="OS version" rows={stats?.byOs} />
          <KeyCountTable title="Network" rows={stats?.byNetwork} />
        </div>
      </div>

      {/* 5. Devices */}
      <div style={card}>
        <h5 style={{ margin: '0 0 10px' }}>5. Devices ({devices.count || 0})</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 10 }}>
          <input style={textInput} placeholder="Search username / device id" value={filters.q} onChange={(e) => setFilter('q', e.target.value)} />
          <select style={textInput} value={filters.build} onChange={(e) => setFilter('build', e.target.value)}>
            <option value="">Any build</option>
            {(stats?.byVersion || []).map((v) => (
              <option key={v.buildNumber} value={v.buildNumber}>{v.buildNumber}{v.appVersion ? ` (${v.appVersion})` : ''}</option>
            ))}
          </select>
          <select style={textInput} value={filters.platform} onChange={(e) => setFilter('platform', e.target.value)}>
            <option value="">Any platform</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
          <input style={textInput} placeholder="Model / brand" value={filters.model} onChange={(e) => setFilter('model', e.target.value)} />
          <input style={textInput} placeholder="Network (wifi, cellular, carrier)" value={filters.network} onChange={(e) => setFilter('network', e.target.value)} />
          <select style={textInput} value={filters.outdated} onChange={(e) => setFilter('outdated', e.target.value)}>
            <option value="">All versions</option>
            <option value="true">Outdated only</option>
            <option value="false">Up to date only</option>
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>User</th><th style={th}>Device</th><th style={th}>Version</th><th style={th}>Network</th>
                <th style={th}>IP</th><th style={th}>Last seen</th><th style={th}>Seen</th>
              </tr>
            </thead>
            <tbody>
              {devices.rows.length ? devices.rows.map((d) => (
                <tr key={d._id} style={{ background: latest && d.buildNumber < latest ? '#fffbeb' : undefined }}>
                  <td style={td}>
                    {d.role === 'anonymous' ? <span style={{ color: '#9ca3af' }}>not signed in</span> : (
                      <>
                        <strong>{d.username || '—'}</strong>
                        <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>{d.name}</span>
                      </>
                    )}
                    <Badge colour={d.role === 'admin' ? '#7c3aed' : d.role === 'staff' ? '#0891b2' : '#6b7280'}>{d.role}</Badge>
                  </td>
                  <td style={td}>
                    {[d.brand, d.model].filter(Boolean).join(' ') || '—'}
                    <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>{d.platform} {d.osVersion}</span>
                    <span style={{ display: 'block', fontSize: 10, color: '#9ca3af', fontFamily: 'monospace', wordBreak: 'break-all' }}>{d.deviceId}</span>
                  </td>
                  <td style={td}>
                    {d.appVersion || '—'} <span style={{ color: '#6b7280' }}>({d.buildNumber})</span>
                    {latest && d.buildNumber < latest ? <div><Badge colour="#d97706">outdated</Badge></div> : null}
                  </td>
                  <td style={td}>{d.networkType || '—'}{d.carrier ? <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>{d.carrier}</span> : null}</td>
                  <td style={td}>{d.ip || '—'}</td>
                  <td style={td}>{fmtDate(d.lastSeenAt)}</td>
                  <td style={td}>{d.seenCount}</td>
                </tr>
              )) : (
                <tr><td style={{ ...td, color: '#9ca3af' }} colSpan={7}>No devices match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 10 }}>
          <button type="button" style={smallBtn} disabled={devicePage <= 1} onClick={() => setDevicePage((p) => Math.max(1, p - 1))}>Previous</button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Page {devices.page} of {devices.pages}</span>
          <button type="button" style={smallBtn} disabled={devicePage >= devices.pages} onClick={() => setDevicePage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}
